import { useState, useEffect } from 'react'; 
import { useParams, useNavigate } from 'react-router-dom';
import { FaLock, FaEye, FaEyeSlash, FaSadCry, FaSpinner } from 'react-icons/fa';
import styles from '../SignIn.module.css';

const CambiarContrasena = () => {
    const { token } = useParams(); 
    const navigate = useNavigate(); 
    const [nuevaContrasena, setNuevaContrasena] = useState(''); 
    const [confirmarContrasena, setConfirmarContrasena] = useState(''); 
    const [cargando, setCargando] = useState(true); 
    const [exito, setExito] = useState(false); 
    const [tokenValido, setTokenValido] = useState(null);
    const [mostrarContrasena, setMostrarContrasena] = useState(false); 
    const [mostrarConfirmar, setMostrarConfirmar] = useState(false);
    const [tiempoRestante, setTiempoRestante] = useState(null);
    
    const [errores, setErrores] = useState({
        nuevaContrasena: '',
        confirmarContrasena: '',
        general: ''
    });

    const validarContrasenaEnTiempoReal = (contrasena, confirmacion) => {
        if (!contrasena) return '';
        
        if (confirmacion && contrasena !== confirmacion) {
            return "Las contraseñas no coinciden.";
        }
        if (contrasena.length < 8) {
            return "La contraseña debe tener al menos 8 caracteres.";
        }
        if (!/[A-Z]/.test(contrasena)) {
            return "La contraseña debe tener una letra mayúscula.";
        }
        if (!/[a-z]/.test(contrasena)) {
            return "La contraseña debe tener una letra minúscula.";
        }
        if (!/[0-9]/.test(contrasena)) {
            return "La contraseña debe tener un número.";
        }
        if (!/[\W_]/.test(contrasena)) {
            return "La contraseña debe tener un carácter especial.";
        }
        return '';
    };

    useEffect(() => { 
        if (!token) {
            setTokenValido(false);
            setErrores(prev => ({ 
                ...prev, 
                general: "Token no encontrado en la URL." 
            }));
            return;
        }

        setCargando(true);
        
        fetch(`${process.env.REACT_APP_API_URL}/auth/validar-token/${token}`, {
            method: 'GET', 
            headers: {
                'Content-Type': 'application/json', 
            }, 
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Error HTTP: ${response.status}`);
            }
            return response.json(); 
        })
        .then(data => {
            if (data.valido) {
                setTokenValido(true);
                setTiempoRestante(data.tiempoRestante);
                setErrores(prev => ({ ...prev, general: '' }));
            } else {
                setTokenValido(false); 
                setErrores(prev => ({ 
                    ...prev, 
                    general: data.mensaje || "El enlace ha expirado o no es válido. Por favor solicita uno nuevo."
                }));
            }
        })
        .catch(error => { 
            console.error('Error verificando token:', error); 
            setTokenValido(false); 
            setErrores(prev => ({ 
                ...prev, 
                general: "Error al verificar el token. Por favor intenta nuevamente."
            }));
        })
        .finally(() => {
            setCargando(false);
        });
    }, [token]);

    const handleChangeNuevaContrasena = (e) => {
        const valor = e.target.value;
        setNuevaContrasena(valor);
        setErrores(prev => ({
            ...prev,
            nuevaContrasena: validarContrasenaEnTiempoReal(valor, confirmarContrasena)
        }));
    };

    const handleChangeConfirmarContrasena = (e) => {
        const valor = e.target.value;
        setConfirmarContrasena(valor);
        setErrores(prev => ({
            ...prev,
            confirmarContrasena: validarContrasenaEnTiempoReal(nuevaContrasena, valor)
        }));
    };

    const toggleMostrarContrasena = () => { 
        setMostrarContrasena(!mostrarContrasena); 
    }; 

    const toggleMostrarConfirmar = () => { 
        setMostrarConfirmar(!mostrarConfirmar); 
    }; 

    const handleSubmit = (e) => { 
        e.preventDefault(); 
        
        setCargando(true);

        const errorNuevaContrasena = validarContrasenaEnTiempoReal(nuevaContrasena, confirmarContrasena);
        const errorConfirmarContrasena = !confirmarContrasena ? 'Confirma tu contraseña.' : '';

        if (errorNuevaContrasena || errorConfirmarContrasena) {
            setErrores({
                nuevaContrasena: errorNuevaContrasena,
                confirmarContrasena: errorConfirmarContrasena,
                general: ''
            });
            setCargando(false);
            return;
        }

        fetch(`${process.env.REACT_APP_API_URL}/auth/cambiar-contrasena`, { 
            method: 'POST', 
            headers: {
                'Content-Type': 'application/json', 
            },
            body: JSON.stringify({ 
                token, 
                nuevaContrasena
            }),
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Error HTTP: ${response.status}`);
            }
            return response.json(); 
        })
        .then(data => {
            setExito(true); 
            setTimeout(() => { 
                navigate('/signin'); 
            }, 3000);
            
        })
        .catch(error => { 
            console.error('Error al cambiar contraseña:', error); 
            setErrores(prev => ({
                ...prev,
                general: 'No se pudo cambiar la contraseña. El enlace puede haber expirado.'
            }));
        })
        .finally(() => {
            setCargando(false);
        }); 
    };

    const formularioValido = !validarContrasenaEnTiempoReal(nuevaContrasena, confirmarContrasena) && 
                            nuevaContrasena.length > 0 && 
                            confirmarContrasena.length > 0;

    if (tokenValido === null) {
        return (
            <div className={styles.loginCuerpo}>
                <div className={styles.loginCuadro}>
                    <div>
                        <FaSpinner className={styles.spinner}/> {' '}
                        <p>Verificando enlace...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.loginCuerpo}>
            <div className={styles.loginCuadro}>
                <h1 className={styles.cambiarCon}>Cambiar Contraseña</h1>
                
                {!tokenValido ? (
                    <div className="token-invalido">
                        <FaSadCry className={styles.iconoCorreo} /> 
                        <p>{errores.general}</p>
                        <div className={styles.volverLink}>
                            <a href="/recuperar-contrasena">Solicitar un nuevo enlace</a>
                        </div>
                    </div>
                ) : exito ? (
                    <div className="exito-container">
                        <h2>¡Contraseña actualizada!</h2>
                        <p>
                            Tu contraseña ha sido cambiada exitosamente. Serás redirigido a la página de inicio de sesión en unos segundos.
                        </p>
                        <div className="volver-login">
                            <a href="/signin">Ir a iniciar sesión</a>
                        </div>
                    </div>
                ) : (
                    <>
                        <p>
                            Crea una nueva contraseña segura para tu cuenta.
                        </p>
                        
                        {errores.general && (
                            <div className={styles.mensajeError}>
                                {errores.general}
                            </div>
                        )}
                        
                        <form onSubmit={handleSubmit} className={styles.loginForm}>
                            <div className={styles.campoLogin}>
                                <label htmlFor="nuevaContrasena">
                                    <FaLock className={styles.iconoInput}/>
                                    Nueva contraseña:
                                </label>
                                <div className={styles.contenedorPassword}>
                                    <input
                                        type={mostrarContrasena ? "text" : "password"}
                                        id="nuevaContrasena"
                                        className={styles.inputLogin}
                                        value={nuevaContrasena}
                                        onChange={handleChangeNuevaContrasena}
                                        required
                                        minLength={8}
                                    />
                                    <button 
                                        type="button" 
                                        className={styles.botonMostrar} 
                                        onClick={toggleMostrarContrasena}
                                        aria-label={mostrarContrasena ? "Ocultar contraseña" : "Mostrar contraseña"}
                                    >
                                        {mostrarContrasena ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                                
                                {errores.nuevaContrasena && (
                                    <p className={styles.mensajeError}>
                                        {errores.nuevaContrasena}
                                    </p>
                                )}
                            </div>
                            
                            <div className={styles.campoLogin}>
                                <label htmlFor="confirmarContrasena">
                                    <FaLock className={styles.iconoInput} />
                                    Confirmar contraseña:
                                </label>
                                <div className={styles.contenedorPassword}>
                                    <input
                                        type={mostrarConfirmar ? "text" : "password"}
                                        id="confirmarContrasena"
                                        className={styles.inputLogin}
                                        value={confirmarContrasena}
                                        onChange={handleChangeConfirmarContrasena}
                                        required
                                    />
                                    <button 
                                        type="button" 
                                        className={styles.botonMostrar} 
                                        onClick={toggleMostrarConfirmar}
                                        aria-label={mostrarConfirmar ? "Ocultar contraseña" : "Mostrar contraseña"}
                                    >
                                        {mostrarConfirmar ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                                
                                {errores.confirmarContrasena && (
                                    <p className={styles.mensajeError}>
                                        {errores.confirmarContrasena}
                                    </p>
                                )}
                            </div>
                            
                            <button 
                                type="submit" 
                                className={styles.botonLogin}
                                disabled={cargando || !formularioValido}
                            >
                                {cargando ? (
                                    <>
                                        <FaSpinner className={styles.spinner}/> {' '}
                                        Cambiando...
                                    </>
                                ) : (
                                    'Cambiar contraseña'
                                )}
                            </button>
                        </form>
                        
                        <div className={styles.volverLink}>
                            <a href="/signin">Volver a iniciar sesión</a>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CambiarContrasena;